import { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, X, Plus, Tag, Check, ChevronsUpDown, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
} from './ui/command';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from './ui/popover';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = {
    actor: 'Actor (Cliente)',
    defendant: 'Demandado (Contraparte)',
    reference: 'Referencia',
    other: 'Otro'
};

const ROLE_COLORS = {
    actor: 'bg-blue-100 text-blue-700 border-blue-200',
    defendant: 'bg-red-100 text-red-700 border-red-200',
    reference: 'bg-gray-100 text-gray-700 border-gray-200',
    other: 'bg-yellow-100 text-yellow-700 border-yellow-200'
};

export const LinkTrademarksModal = ({ procedureId, clientId, token, open, onClose, onLinksUpdated, currentLinks = [] }) => {
    const [trademarks, setTrademarks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Form state for adding new link
    const [selectedTrademarkId, setSelectedTrademarkId] = useState(undefined);
    const [selectedRole, setSelectedRole] = useState('actor');
    const [notes, setNotes] = useState('');
    const [openCombobox, setOpenCombobox] = useState(false);

    // Counterparty creation state
    const [showCounterpartyForm, setShowCounterpartyForm] = useState(false);
    const [cpForm, setCpForm] = useState({ expediente: '', denominacion: '' });
    const [creatingCp, setCreatingCp] = useState(false);

    // Local state for links
    const [links, setLinks] = useState(currentLinks);

    useEffect(() => {
        if (open && clientId) {
            fetchTrademarks();
            setShowCounterpartyForm(false);
            setCpForm({ expediente: '', denominacion: '' });
        }
        setLinks(currentLinks);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, clientId]);

    async function fetchTrademarks() {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/trademarks?client_id=${clientId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTrademarks(res.data.filter(tm => tm.is_active !== false));
        } catch (error) {
            console.error('Error fetching trademarks:', error);
            toast.error('Error al cargar marcas');
        } finally {
            setLoading(false);
        }
    }

    const getTrademarkName = (trademarkId) => {
        const tm = trademarks.find(t => t.id === trademarkId);
        return tm ? (tm.denominacion || tm.expediente) : trademarkId;
    };

    const handleAddLink = () => {
        if (!selectedTrademarkId) {
            toast.error('Selecciona una marca');
            return;
        }

        if (links.some(link => link.trademark_id === selectedTrademarkId)) {
            toast.error('Esta marca ya está vinculada');
            return;
        }

        const newLink = {
            trademark_id: selectedTrademarkId,
            role: selectedRole,
            notes: notes || null
        };

        setLinks([...links, newLink]);
        resetForm();
        toast.success('Marca agregada a la lista');
    };

    const resetForm = () => {
        setSelectedTrademarkId(undefined);
        setSelectedRole('actor');
        setNotes('');
        setShowCounterpartyForm(false);
    };

    const handleRemoveLink = (trademarkId) => {
        setLinks(links.filter(link => link.trademark_id !== trademarkId));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/procedures/${procedureId}`, {
                trademark_links: links
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success('Marcas vinculadas actualizadas');
            if (onLinksUpdated) onLinksUpdated();
            onClose();
        } catch (error) {
            console.error('Error updating trademark links:', error);
            toast.error('Error al actualizar vínculos');
        } finally {
            setSaving(false);
        }
    };

    const handleCreateCounterparty = async () => {
        if (!cpForm.expediente.trim() || !cpForm.denominacion.trim()) {
            toast.error('Expediente y Denominación son requeridos');
            return;
        }

        setCreatingCp(true);
        try {
            // Create minimal trademark
            const newTm = {
                client_id: clientId,
                expediente: cpForm.expediente.trim(),
                denominacion: cpForm.denominacion.trim(),
                registro: null, // Unknown
                tipo: 'DENOMINATIVA', // Default
                clases: [],
                titular_nombre: 'CONTRA-PARTE', // Optional hint?
                estatus_impi_crudo: 'NUEVA_MONITOREO' // Flag
            };

            const res = await axios.post(`${API}/trademarks`, newTm, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const createdTm = res.data;

            // Add to local list and select it
            setTrademarks([...trademarks, createdTm]);
            setSelectedTrademarkId(createdTm.id);
            setSelectedRole('defendant'); // Default role for counterparty
            setNotes('Marca opositora registrada para monitoreo');
            setShowCounterpartyForm(false);
            setCpForm({ expediente: '', denominacion: '' });

            toast.success('Marca creada y seleccionada');

        } catch (error) {
            console.error('Error creating counterparty trademark:', error);
            toast.error('Error al crear marca');
        } finally {
            setCreatingCp(false);
        }
    };

    const getAvailableTrademarks = () => {
        const linkedIds = new Set(links.map(link => link.trademark_id));
        return trademarks.filter(tm => !linkedIds.has(tm.id));
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Tag className="h-5 w-5" />
                        Gestionar Marcas Vinculadas
                    </DialogTitle>
                    <DialogDescription>
                        Vincula marcas al procedimiento. Puedes buscar por expediente o nombre, o registrar nuevas marcas de contraparte.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 py-4">
                    {/* Current Links */}
                    <div>
                        <Label className="text-sm font-medium mb-2 block">
                            Marcas Vinculadas ({links.length})
                        </Label>
                        {links.length === 0 ? (
                            <p className="text-sm text-gray-500 py-4 text-center border rounded-lg bg-gray-50 border-dashed">
                                No hay marcas vinculadas actualmente
                            </p>
                        ) : (
                            <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
                                {links.map(link => (
                                    <div key={link.trademark_id} className="px-4 py-3 flex items-start justify-between hover:bg-gray-50">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-sm font-medium text-gray-900">
                                                    {getTrademarkName(link.trademark_id)}
                                                </span>
                                                <Badge className={`${ROLE_COLORS[link.role]} border text-xs`}>
                                                    {ROLE_LABELS[link.role]}
                                                </Badge>
                                            </div>
                                            {link.notes && (
                                                <p className="text-xs text-gray-500 mt-1">{link.notes}</p>
                                            )}
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleRemoveLink(link.trademark_id)}
                                            className="h-8 w-8 p-0 text-gray-400 hover:text-red-600"
                                        >
                                            <X className="h-4 w-4" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Add Section */}
                    <div className="border-t pt-4">
                        <div className="flex items-center justify-between mb-3">
                            <Label className="text-sm font-medium">Agregar Marca</Label>
                            {!showCounterpartyForm && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-xs h-7 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                                    onClick={() => setShowCounterpartyForm(true)}
                                >
                                    <Plus className="h-3 w-3 mr-1" />
                                    Registrar Marca Contraria
                                </Button>
                            )}
                        </div>

                        {showCounterpartyForm ? (
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-3 animation-in slide-in-from-top-2 duration-200">
                                <div className="flex items-center justify-between">
                                    <h4 className="text-sm font-medium text-gray-900">Nueva Marca de Monitoreo (Contraparte)</h4>
                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setShowCounterpartyForm(false)}>
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-1">
                                        <Label className="text-xs">Expediente *</Label>
                                        <Input
                                            value={cpForm.expediente}
                                            onChange={e => setCpForm({ ...cpForm, expediente: e.target.value })}
                                            placeholder="1234567"
                                            className="h-8"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs">Denominación *</Label>
                                        <Input
                                            value={cpForm.denominacion}
                                            onChange={e => setCpForm({ ...cpForm, denominacion: e.target.value })}
                                            placeholder="MARCA EJEMPLO"
                                            className="h-8"
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end pt-2">
                                    <Button size="sm" onClick={handleCreateCounterparty} disabled={creatingCp}>
                                        {creatingCp && <Loader2 className="h-3 w-3 mr-2 animate-spin" />}
                                        Crear y Seleccionar
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {/* Searchable Select (Combobox) */}
                                <div>
                                    <Label className="text-xs text-gray-500 mb-1 block">Buscar por nombre o expediente</Label>
                                    <Popover open={openCombobox} onOpenChange={setOpenCombobox}>
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="outline"
                                                role="combobox"
                                                aria-expanded={openCombobox}
                                                className="w-full justify-between font-normal text-left"
                                            >
                                                {selectedTrademarkId
                                                    ? getTrademarkName(selectedTrademarkId)
                                                    : "Seleccionar marca..."}
                                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-full p-0">
                                            <Command>
                                                <CommandInput placeholder="Buscar marca..." />
                                                <CommandEmpty>No se encontró la marca.</CommandEmpty>
                                                <CommandGroup className="max-h-60 overflow-y-auto">
                                                    {getAvailableTrademarks().map((tm) => (
                                                        <CommandItem
                                                            key={tm.id}
                                                            value={`${tm.denominacion} ${tm.expediente}`}
                                                            onSelect={() => {
                                                                setSelectedTrademarkId(tm.id);
                                                                setOpenCombobox(false);
                                                            }}
                                                        >
                                                            <Check
                                                                className={cn(
                                                                    "mr-2 h-4 w-4",
                                                                    selectedTrademarkId === tm.id ? "opacity-100" : "opacity-0"
                                                                )}
                                                            />
                                                            <div className="flex flex-col">
                                                                <span className="font-medium">{tm.denominacion}</span>
                                                                <span className="text-xs text-gray-500">{tm.expediente || 'S/N'}</span>
                                                            </div>
                                                        </CommandItem>
                                                    ))}
                                                </CommandGroup>
                                            </Command>
                                        </PopoverContent>
                                    </Popover>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label htmlFor="role" className="text-xs text-gray-500">Rol</Label>
                                        <Select value={selectedRole} onValueChange={setSelectedRole}>
                                            <SelectTrigger className="mt-1 h-9">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="actor">Actor (Cliente)</SelectItem>
                                                <SelectItem value="defendant">Demandado (Contraparte)</SelectItem>
                                                <SelectItem value="reference">Referencia</SelectItem>
                                                <SelectItem value="other">Otro</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <div className="space-y-1">
                                            <Label htmlFor="notes" className="text-xs text-gray-500">Notas</Label>
                                            <Input
                                                id="notes"
                                                value={notes}
                                                onChange={(e) => setNotes(e.target.value)}
                                                placeholder="Opcional"
                                                className="h-9"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <Button
                                    onClick={handleAddLink}
                                    disabled={!selectedTrademarkId}
                                    variant="outline"
                                    size="sm"
                                    className="w-full bg-gray-50 hover:bg-gray-100 border-dashed"
                                >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Vincular Marca Seleccionada
                                </Button>
                            </div>
                        )}

                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>
                        Cancelar
                    </Button>
                    <Button onClick={handleSave} disabled={saving}>
                        {saving ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Guardando...
                            </>
                        ) : (
                            'Guardar Cambios'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
